import { withScope, captureException } from '@sentry/browser';

import {
    SentryUnknownError,
    SentryEmptyError,
    SentrySimpleDataError,
    SentryComplexDataError,
} from '../errors/sentryErrors';

// For JavaScript data types please reference the documentation below:
// @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Data_structures
// @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/typeof

export function capture(thing: unknown): void {
    // istanbul ignore else
    if (thing === null || typeof thing === 'undefined') {
        captureException(new SentryEmptyError());
    } else if (thing instanceof Error || Object.prototype.hasOwnProperty.call(thing, 'stack')) {
        captureException(thing);
    } else if (
        typeof thing === 'string' ||
        thing instanceof String ||
        typeof thing === 'number' ||
        thing instanceof Number ||
        typeof thing === 'bigint' ||
        typeof thing === 'symbol'
    ) {
        captureException(new SentrySimpleDataError(String(thing)));
    } else if (typeof thing === 'object') {
        withScope(scope => {
            scope.setContext('Unknown Data', { data: thing });
            captureException(new SentryComplexDataError('object'));
        });
    } else if (typeof thing === 'function') {
        const name = `[Function ${thing.name || 'anonymous'}]`;
        withScope(scope => {
            scope.setContext('Unknown Data', { data: name });
            captureException(new SentryComplexDataError(name));
        });
    } else {
        // We don't know what this is so log it and hope we can
        // follow the stack traces to determine what is causing it
        // in order to handle it better. Ideally this should never
        // happen but we also don't want a case where a captured
        // error will go unreported so we should have it just in case.
        //
        // NOTE: This else statement is untested, if we new how to
        // test it then we wouldn't need it...
        captureException(new SentryUnknownError(typeof thing));
    }
}
